//adminfrontend/src/admin_ui/components/AdminNav.jsx
import React, { useEffect, useMemo, useRef, useState,  } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../styles/AdminNav.css";

function AdminNav({ adminEmail, onLogout }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, swiping: false });

  const links = useMemo(
    () => [
      { to: "/applications", label: "Applications" },
      { to: "/audit-logs", label: "Audit Logs" },
      { to: "/risk-events", label: "Risk Events" },
      { to: "/metered", label: "Metered" },
      { to: "/analytics/forecast", label: "Analytics — Forecast" },
      { to: "/compliance/overview", label: "Compliance — Overview" },
      { to: "/compliance/verifications", label: "Compliance — Verifications" },
      { to: "/compliance/shipments", label: "Compliance — Shipments" },
      { to: "/users", label: "Create User" },
      { to: "/branches", label: "Branches" },
      { to: "/locations", label: "Locations" },
      { to: "/stock-levels", label: "Stock Levels" },
      { to: "/stock-movements", label: "Stock Movements" },
      { to: "/receive-manufactured", label: "Receive Stock" },
      { to: "/suppliers", label: "Suppliers" },
      { to: "/purchase-orders", label: "Purchase Orders" },
      { to: "/goods-receipts", label: "Goods Receipts" },
      { to: "/logistics/shipments", label: "Shipments" },
      { to: "/logistics/shipment-approvals", label: "Shipment Approvals" },
      { to: "/pos-terminals", label: "POS Terminals" },
      { to: "/pos-shifts", label: "POS Shifts" },
      { to: "/pos-sales", label: "POS Sales" },
      { to: "/pos-inventory-approvals", label: "POS Inventory Approvals" },
      { to: "/accounting/journal-entries", label: "Journal Entries" },
      { to: "/accounting/trial-balance", label: "Trial Balance" },
      { to: "/accounting/pnl", label: "Profit & Loss" },
      { to: "/accounting/tax-summary", label: "Tax Summary" },
    ],
    []
  );

  // Close mobile menu whenever route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Swipe-to-close (drawer opens from right; swipe right closes)
  function onDrawerTouchStart(e) {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, swiping: true };
  }

  function onDrawerTouchMove(e) {
    if (!touchRef.current.swiping) return;
    const t = e.touches && e.touches[0];
    if (!t) return;

    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;

    // If it's clearly a vertical scroll, stop treating it as a swipe gesture.
    if (Math.abs(dy) > 18 && Math.abs(dy) > Math.abs(dx)) {
      touchRef.current.swiping = false;
      return;
    }

    // Swipe right threshold
    if (dx > 80) {
      touchRef.current.swiping = false;
      setOpen(false);
    }
  }

  function onDrawerTouchEnd() {
    touchRef.current.swiping = false;
  }

  return (
    <>
      <header className="ucx-admin-topbar">
        <div className="ucx-container ucx-admin-topbar-inner">
          <div className="ucx-admin-brand">
          <div className="ucx-admin-brand-mark" aria-hidden="true">
            UCX
          </div>
          <div className="ucx-admin-brand-text">
            <div className="ucx-admin-brand-title">UNI-CODEX Admin</div>
            <div className="ucx-admin-brand-subtitle">Secure Console</div>
          </div>
          </div>

        <button
          type="button"
          className={`ucx-admin-nav-toggle ${open ? "is-open" : ""}`}
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="ucx-admin-nav-toggle-bar" />
          <span className="ucx-admin-nav-toggle-bar" />
          <span className="ucx-admin-nav-toggle-bar" />
        </button>

        {/* Desktop nav stays in the header */}
          <nav className="ucx-admin-nav ucx-admin-nav--desktop">
            <div className="ucx-admin-nav-links" role="navigation" aria-label="Admin navigation">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `ucx-admin-navlink ${isActive ? "is-active" : ""}`
                  }
                  title={l.label}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>

            <div className="ucx-admin-nav-right">
              {adminEmail ? (
                <span className="ucx-admin-nav-email" title={adminEmail}>
                  {adminEmail}
                </span>
              ) : null}

              <button
                type="button"
                className="ucx-btn ucx-btn-outline ucx-admin-nav-logout"
                onClick={onLogout}
              >
                Sign out
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Backdrop (always mounted so it can animate out) */}
      <button
        type="button"
        className={`ucx-admin-nav-backdrop ${open ? "is-open" : ""}`}
        aria-label="Close menu"
        aria-hidden={open ? "false" : "true"}
        tabIndex={open ? 0 : -1}
        onClick={() => setOpen(false)}
      />

      {/* Mobile drawer (separate from header so fixed positioning is reliable) */}
      <nav
        className={`ucx-admin-nav-drawer ${open ? "is-open" : ""}`}
        aria-label="Admin navigation (mobile)"
        onTouchStart={onDrawerTouchStart}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
      >
        <div className="ucx-admin-nav-drawer-head">
          <div className="ucx-admin-nav-drawer-title">Menu</div>
          <button
            type="button"
            className="ucx-admin-drawer-close"
            aria-label="Close navigation"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        <div className="ucx-admin-nav-links ucx-admin-nav-links--drawer">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `ucx-admin-navlink ${isActive ? "is-active" : ""}`
              }
              title={l.label}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="ucx-admin-nav-right ucx-admin-nav-right--drawer">
          {adminEmail ? (
            <span className="ucx-admin-nav-email" title={adminEmail}>
              {adminEmail}
            </span>
          ) : null}

          <button
            type="button"
            className="ucx-btn ucx-btn-outline ucx-admin-nav-logout"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sign out
          </button>
        </div>
      </nav>
    </>
  );
}

export default AdminNav;
